import { useLayoutEffect, useState } from "react";

import { ThemeProviderContext } from "@/components/theme-provider-context";
import { applyThemeSnapshot, themeSnapshot } from "@/themes/apply-theme";
import { DEFAULT_THEME_ID, getTheme, isThemeId, type ThemeId } from "@/themes/registry";
import type { ThemeSnapshot } from "@/themes/types";

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: ThemeId;
  storageKey?: string;
};

// A retired theme falls back to the base its snapshot recorded, which the pre-React script already painted
const snapshotBase = (storageKey: string): ThemeId | null => {
  try {
    const snapshot = JSON.parse(localStorage.getItem(`${storageKey}-snapshot`) ?? "null") as Partial<ThemeSnapshot> | null;
    const base = snapshot?.base ?? null;
    return isThemeId(base) ? base : null;
  } catch {
    return null;
  }
};

export function ThemeProvider({
  children,
  defaultTheme = DEFAULT_THEME_ID,
  storageKey = "vite-ui-theme",
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<ThemeId>(() => {
    const stored = localStorage.getItem(storageKey);
    if (isThemeId(stored)) return stored;

    return snapshotBase(storageKey) ?? defaultTheme;
  });
  // useLayoutEffect (not useEffect) so the class is applied before the browser
  // paints. The `theme-switching` class freezes transitions for the swap (see
  // index.css) so the whole UI snaps to the new theme instead of cross-fading
  // every transition-colors element.
  useLayoutEffect(() => {
    const root = window.document.documentElement;
    const snapshot = themeSnapshot(getTheme(theme));

    root.classList.add("theme-switching");
    applyThemeSnapshot(root, snapshot);
    // Read by the pre-React script in index.html on the next load
    localStorage.setItem(`${storageKey}-snapshot`, JSON.stringify(snapshot));
    void root.offsetHeight; // flush the frozen state before re-enabling transitions
    requestAnimationFrame(() => root.classList.remove("theme-switching"));
  }, [theme, storageKey]);

  const value = {
    theme,
    setTheme: (theme: ThemeId) => {
      localStorage.setItem(storageKey, theme);
      setTheme(theme);
    },
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}
